/**
 * VenueTourBooking - Schedule venue tour/visit before booking
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Calendar } from '../ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { 
  Calendar as CalendarIcon, Clock, Eye, Check, ChevronRight, Loader2,
  Users, Phone, Video, Building
} from 'lucide-react';
import { format, addDays } from 'date-fns';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Time slots for tours
const TOUR_SLOTS = [
  { id: 'morning_1', time: '10:00', label: '10:00 AM' },
  { id: 'morning_2', time: '11:00', label: '11:00 AM' },
  { id: 'afternoon_1', time: '14:00', label: '2:00 PM' },
  { id: 'afternoon_2', time: '15:00', label: '3:00 PM' },
  { id: 'afternoon_3', time: '16:00', label: '4:00 PM' },
  { id: 'evening', time: '17:00', label: '5:00 PM' }
];

// Tour types
const TOUR_TYPES = [
  { 
    id: 'in_person', 
    name: 'In-Person Visit', 
    icon: Building,
    description: 'Visit the venue in person',
    duration: '30-45 minutes'
  },
  { 
    id: 'virtual', 
    name: 'Virtual Tour', 
    icon: Video,
    description: 'Video call walkthrough',
    duration: '20-30 minutes'
  },
  { 
    id: 'phone', 
    name: 'Phone Consultation', 
    icon: Phone,
    description: 'Discuss venue details over phone',
    duration: '15-20 minutes'
  }
];

export default function VenueTourBooking({ open, onClose, venue, onTourBooked }) {
  const [step, setStep] = useState(1);
  const [tourType, setTourType] = useState('in_person');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [partySize, setPartySize] = useState('2');
  const [eventType, setEventType] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [guestCount, setGuestCount] = useState('');
  const [questions, setQuestions] = useState('');
  const [contactInfo, setContactInfo] = useState({ name: '', phone: '', email: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const tourData = {
        venue_id: venue?.id,
        venue_name: venue?.provider_name,
        tour_type: tourType,
        tour_date: format(selectedDate, 'yyyy-MM-dd'),
        tour_time: TOUR_SLOTS.find(s => s.id === selectedSlot)?.time,
        party_size: parseInt(partySize),
        event_type: eventType,
        tentative_event_date: eventDate,
        expected_guests: guestCount ? parseInt(guestCount) : null,
        questions: questions,
        contact: contactInfo
      };

      const response = await fetch(`${API_URL}/api/events/tours`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tourData)
      });

      if (response.ok) {
        toast.success('Tour scheduled! You\'ll receive confirmation shortly.');
        onTourBooked?.();
        onClose();
      } else {
        throw new Error('Booking failed');
      }
    } catch (error) {
      toast.error('Failed to schedule tour. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectedTourType = TOUR_TYPES.find(t => t.id === tourType);

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">How would you like to see the venue?</h3>
            
            <RadioGroup value={tourType} onValueChange={setTourType} className="space-y-3">
              {TOUR_TYPES.map(type => {
                const Icon = type.icon;
                return (
                  <div key={type.id}>
                    <RadioGroupItem value={type.id} id={type.id} className="peer sr-only" />
                    <Label
                      htmlFor={type.id}
                      className="flex items-center gap-4 p-4 border rounded-lg cursor-pointer peer-data-[state=checked]:border-purple-500 peer-data-[state=checked]:bg-purple-50"
                    >
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <Icon className="w-5 h-5 text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{type.name}</p>
                        <p className="text-sm text-gray-500">{type.description}</p>
                        <p className="text-xs text-gray-400">Duration: {type.duration}</p>
                      </div>
                      <div className="w-4 h-4 rounded-full border-2 peer-data-[state=checked]:bg-purple-600 peer-data-[state=checked]:border-purple-600" />
                    </Label>
                  </div>
                );
              })}
            </RadioGroup>

            {tourType === 'in_person' && (
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                <p className="text-sm text-purple-800">
                  <Eye className="w-4 h-4 inline mr-2" />
                  See the space, meet the team, and visualize your event!
                </p>
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Choose Date & Time</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Select Date</label>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => {
                    const day = date.getDay();
                    return date < new Date() || date > addDays(new Date(), 30);
                  }}
                  className="rounded-md border"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Available Slots</label>
                <div className="space-y-2">
                  {TOUR_SLOTS.map(slot => (
                    <button
                      key={slot.id}
                      onClick={() => setSelectedSlot(slot.id)}
                      className={`w-full p-3 rounded-lg border text-left transition-all ${
                        selectedSlot === slot.id
                          ? 'bg-purple-600 text-white border-purple-600'
                          : 'hover:border-purple-300'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span className="font-medium">{slot.label}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {tourType === 'in_person' && (
              <div>
                <label className="text-sm font-medium text-gray-700">How many people will attend?</label>
                <Select value={partySize} onValueChange={setPartySize}>
                  <SelectTrigger className="mt-1">
                    <Users className="w-4 h-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Just me</SelectItem>
                    <SelectItem value="2">2 people</SelectItem>
                    <SelectItem value="3">3 people</SelectItem>
                    <SelectItem value="4">4 people</SelectItem>
                    <SelectItem value="5">5+ people</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {selectedDate && selectedSlot && (
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <p className="text-green-800">
                  <Check className="w-4 h-4 inline mr-2" />
                  {selectedTourType?.name} on {format(selectedDate, 'EEEE, MMMM d, yyyy')} at {TOUR_SLOTS.find(s => s.id === selectedSlot)?.label}
                </p>
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">About Your Event</h3>
            <p className="text-sm text-gray-500">This helps us prepare for your tour</p>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Event Type</label>
                <Select value={eventType} onValueChange={setEventType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="wedding">Wedding</SelectItem>
                    <SelectItem value="corporate">Corporate Event</SelectItem>
                    <SelectItem value="birthday">Birthday Party</SelectItem>
                    <SelectItem value="engagement">Engagement</SelectItem>
                    <SelectItem value="conference">Conference</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Tentative Event Date</label>
                <Input
                  type="date"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                  min={format(addDays(new Date(), 30), 'yyyy-MM-dd')}
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Expected Guests</label>
              <Input
                type="number"
                value={guestCount}
                onChange={(e) => setGuestCount(e.target.value)}
                placeholder="e.g., 150"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Questions for the tour?</label>
              <Textarea
                value={questions}
                onChange={(e) => setQuestions(e.target.value)}
                placeholder="Anything specific you'd like to know about the venue..."
                rows={3}
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Your Contact Details</h3>

            <div className="space-y-3">
              <Input
                placeholder="Your full name *"
                value={contactInfo.name}
                onChange={(e) => setContactInfo(c => ({ ...c, name: e.target.value }))}
              />
              <Input
                placeholder="Phone number *"
                value={contactInfo.phone}
                onChange={(e) => setContactInfo(c => ({ ...c, phone: e.target.value }))}
              />
              <Input
                type="email"
                placeholder="Email address *"
                value={contactInfo.email}
                onChange={(e) => setContactInfo(c => ({ ...c, email: e.target.value }))}
              />
            </div>

            {/* Summary */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
              <h4 className="font-medium">Tour Summary</h4>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-600">Venue</span>
                  <span className="font-medium">{venue?.provider_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tour Type</span>
                  <span className="capitalize">{selectedTourType?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Date & Time</span>
                  <span>
                    {selectedDate && format(selectedDate, 'MMM d, yyyy')} at {TOUR_SLOTS.find(s => s.id === selectedSlot)?.label}
                  </span>
                </div>
                {eventType && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Event</span>
                    <span className="capitalize">{eventType}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-sm text-purple-800">
                <Building className="w-4 h-4 inline mr-2" />
                You'll receive a confirmation email with venue directions and contact details.
              </p>
            </div>
          </div>
        );
    }
  };

  if (!venue) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-purple-600" />
            Schedule a Venue Tour
          </DialogTitle>
          <DialogDescription>{venue.provider_name}</DialogDescription>
        </DialogHeader>

        {/* Progress */}
        <div className="flex items-center gap-2 mb-4">
          {[1, 2, 3, 4].map((s) => (
            <React.Fragment key={s}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                s < step ? 'bg-purple-600 text-white' :
                s === step ? 'bg-purple-100 text-purple-700 border-2 border-purple-600' :
                'bg-gray-100 text-gray-400'
              }`}>
                {s < step ? <Check className="w-4 h-4" /> : s}
              </div>
              {s < 4 && <div className={`flex-1 h-1 ${s < step ? 'bg-purple-600' : 'bg-gray-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        {renderStep()}

        <div className="flex justify-between pt-4 border-t">
          {step > 1 ? (
            <Button variant="outline" onClick={() => setStep(s => s - 1)}>Back</Button>
          ) : <div />}

          {step < 4 ? (
            <Button
              onClick={() => setStep(s => s + 1)}
              disabled={
                (step === 2 && (!selectedDate || !selectedSlot))
              }
              className="bg-purple-600 hover:bg-purple-700"
            >
              Continue
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={loading || !contactInfo.name || !contactInfo.phone || !contactInfo.email}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Schedule Tour
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
